import Irradiance from 'components/Irradiance';
import MonthButtons from 'components/Irradiance/MonthButtons';
import Loader, { Loader_Type } from 'components/Loader';
import MoveButton from 'components/MoveButton';
import RotateButton from 'components/RotateButton';
import CompassButton from 'components/CompassButton';
import ZoomButtons from 'components/ZoomButtons';

import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setKonvaStagePosition, setMultiFingersPress, setSelectedObstructionId, setSelectedRoofIndex, setStageRotation, setZoomLevel } from 'store/slices/ToolSlice';
import ProductionLegend from './Editor/ProductionLegend';
import PanelDrawer from './Raster/PanelDrawer';
import { FlexDiv, MoveZoomWrapper, StripProductionWrap } from './styles';
import {
	applyDisplacementBound, getLocalPoint, rotateStageAroundPivot, rotateVectorDeg, zoomAtAPosition
} from './utils/util';
import { DESIGN_MODES } from '../../constants';
import { isModeDefault as isModeDefaultUtil } from '../../util';

import Obstructions from '../Obstructions';
import FacetGroup from './Group/FacetGroup';
import SetbackArea from './Facet/SetbackArea';
import SimplifiedConcaveHullLines from './Facet/SimplifiedConcaveHullLines';
import RawConcaveHullLines from './Facet/RawConcaveHullLines';
import CustomVectorPanel from './Panel/CustomVectorPanel';
import CustomPanel from './Panel/CustomPanel';
import ModeStripContainer from '../ModeStrip/ModeStripContainer';

import FullViewBtn from './Editor/ButtonDiv/FullViewBtn';
import GutterLines from './Facet/GutterLines';
import EdgeLength from './Facet/EdgeLength';
import SpecialEdges from './Facet/SpecialEdges/SpecialEdges';
import { useKonvaPositioning } from 'hooks/konva/useKonvaStagePos';
import { useKonvaImageScale, useKonvaStageDims } from 'contexts/ToolDimensionsAndScaleContext';
import { KonvaGroupNames } from './utils';
import { FacetLabels } from './Facet/FacetLabels';
import SelectedMovablePanelsGroup from './Group/PanelsGroup/SelectedMovablePanelsGroup';
import ObstructionDrawer from './Editor/AddObstructionBtn/ObstructionDrawer';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import RasterImage from './Raster/RasterImage';
import { selectCustomMap } from 'store/selectors/customMapSelector';
import CustomMapGroup from './Group/CustomMapGroup';
import TreeDrawer from '../Tree/TreeDrawer';
import FlattenAreaDrawer from '../FlattenArea/FlattenAreaDrawer';
import FlattenAreaFillLayer from '../FlattenArea/FlattenAreaFillLayer';
import { selectIsFlattenAreaMode } from 'store/slices/TreeSlice/selector';
// Lazy: ThreeDView pulls in `three` and the whole 3D subsystem. It only renders
// once the user opts in via Show3DButton, so it loads on demand rather than
// shipping in the entry chunk.
const ThreeDView = lazy(() => import('components/ThreeDImage/ThreeDView'));
import DsmEditsGroup from 'components/Tree';

function Tool() {
	const layerRef = useRef<Konva.Layer | null>(null);
	const { mode, org, designType, dsmUrl } = useSelector((state: RootState) => state.roofData.data);
	const { configuringNewPanel } = useSelector((state: RootState) => state.roofData);
	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const isCanvasLocked = queriesRunningInBackground || configuringNewPanel;
	const { enableEditFacet, pointSelected } = useSelector((state: RootState) => state.roofEditData.data);
	
	const stageRef = useKonvaPositioning();
	const canvasWrapRef = useRef<HTMLDivElement | null>(null);

	// const [threeDModelReady, setThreeDModelReady] = useState(false);
	const lastDistRef = useRef<number | null>(null);
	const lastCenterRef = useRef<{ x: number; y: number } | null>(null);
	// wheel zoom applies at most one step per frame (see handleMouseScroll)
	const wheelFrameRef = useRef<number | null>(null);
	const lastAngleRef = useRef<number | null>(null);
	// mirrors multiFingersPress so touch handlers only dispatch when it actually changes
	const multiFingersActiveRef = useRef(false);

	const toolEditState = useSelector((state: RootState) => state.toolNewPostions.data);
	const { threeDModelEnabled, zoomLevel, moveStage, rotateStage, stageRotation, drawModeEnabled, fullScreenEnabled, enableEditObs,
		groundMountEnabled, newlyCreatedFacets, addObstructionsMode, multiFingersPress, deleteObstructionsMode,
		threeDViewLoaded, selectedRoofIndex, selectedObstructionId, hideSatelliteOverlay } = toolEditState;
	const { activePanelMode, enableAddPanel, enableMultipleAddPanel, deleteIndividualPanelMode, enablePanelMove, selectedMovablePanels }
		= useSelector((state: RootState) => state.panelSlice.data);

	const [enableSingleFinger, setEnableSingleFinger] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const { showIrradiance, showProgress } = useSelector((state: RootState) => state.Irradiance.data);
	const { activeRoofTool } = useSelector((state: RootState) => state.roofToolLines.data);
	const showLoader = (threeDModelEnabled && !threeDViewLoaded) || (showProgress && showIrradiance);
	const isThreeDLoader = threeDModelEnabled && !threeDViewLoaded;
	const konvaStageDims = useKonvaStageDims();
	const [clickedOnPanel, setClickedOnPanel] = useState(false);
	const isModeDefault = isModeDefaultUtil(mode);
	const isEditObsMode = enableEditObs && !deleteObstructionsMode;
	const allowsMultipleNewFacets = isModeDefault;
	const hasReachedNewFacetLimit = !allowsMultipleNewFacets && Object.keys(newlyCreatedFacets).length >= 1;
	const { isImageEditingEnabled: customImageEditingEnabled } = useSelector(selectCustomMap);
	const baseImageScale = useKonvaImageScale();
	const { treeEditingMode, deleteMode } = useSelector((state: RootState)=> state.treeSlice.data);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	// Panel interactions never happen inside tree/obstruction modes, so keep its stage listeners out of the way there.
	const showCustomVectorPanel = designType !== 'ROOF' && !treeEditingMode && !enableEditObs && !addObstructionsMode
		&& !(enablePanelMove && clickedOnPanel);

	const handleMouseScroll = (event: KonvaEventObject<WheelEvent>) => {
		event.evt.preventDefault();
		// trackpads/fast wheels fire many events per frame; each one re-renders every component
		// reading the tool slice. One zoom step per frame keeps the per-notch zoom speed the same.
		if (wheelFrameRef.current !== null) return;
		wheelFrameRef.current = requestAnimationFrame(() => {
			wheelFrameRef.current = null;
		});
		const stage = stageRef.current;
		if (!stage) return;
		const type = event.evt.deltaY > 0 ? 'out' : 'in';
		const updatedZoomLevelAndPositon = zoomAtAPosition(stage, stage.getPointerPosition()!, type);
		if (!updatedZoomLevelAndPositon) return;
		dispatch(setZoomLevel(updatedZoomLevelAndPositon));
	};
	
	useLayoutEffect(() => {
		const canvasWrapElement = canvasWrapRef.current;
		if (!canvasWrapElement) {
			return;
		}
		canvasWrapElement.style.width = `${konvaStageDims.width}px`;
		canvasWrapElement.style.height = `${konvaStageDims.height}px`;
	}, [konvaStageDims]);

	useEffect(() => {
		if (layerRef.current) {
			window.parent.postMessage({ id: 'SOLARQUOTEEMBED_DATA_LOADED' }, '*');
			layerRef.current.getCanvas()._canvas.id = 'SOLARQUOTEEMBED-CANVAS';
		}
	},[]);

	// Irradiance/flux fetch is triggered by irradianceListener (on design-load fulfilled),
	// so it always runs against the freshly-loaded mode — see store/middleware/irradianceListener.ts.

	useEffect(() => {
		const container = stageRef.current?.container();
		if (!container) return;

		const preventLongPress = (e: TouchEvent) => {
			if (e.touches.length === 1) {
				e.preventDefault();
			}
		};

		container.addEventListener('touchstart', preventLongPress, { passive: false });

		return () => container.removeEventListener('touchstart', preventLongPress);
	}, [stageRef]);

	const cursorStyle = useMemo(() => {
		const canDraw = drawModeEnabled && !hasReachedNewFacetLimit;
		switch (true) {
		case rotateStage:
			return 'grab';
		case moveStage:
			return 'grab';
		case treeEditingMode && deleteMode:
			return 'default';
		case treeEditingMode:
			return 'crosshair';
		case !!selectedObstructionId:
			return 'default';
		case isEditObsMode && !selectedObstructionId:
			return 'crosshair';
		case canDraw:
			return 'crosshair';
		// case addObstructionsMode:
		// 	return 'crosshair';
		default:
			return 'default';
		}
	}, [deleteMode, drawModeEnabled, hasReachedNewFacetLimit, isEditObsMode, moveStage, rotateStage, selectedObstructionId, treeEditingMode]);

	function setDragBoundToStage(pos: Vector2d) {
		if (fullScreenEnabled) return pos;
		if (!stageRef.current) return pos;
		// position is stored once on drag end; dispatching here re-rendered the whole tool every frame
		const current = stageRef.current.position();
		const bounded = applyDisplacementBound(stageRef.current, { ...pos });
		// Zoom/pinch can leave the image past the bound; clamping straight to it would snap the image
		// away from the pointer on the first drag frame. Instead, let it stay or move back in, never further out.
		// e.g. bound x = 100, image already at 40: dragging to 20 stays at 40, dragging to 70 moves to 70.
		return {
			x: clampTowardBound(pos.x, current.x, bounded.x),
			y: clampTowardBound(pos.y, current.y, bounded.y),
		};
	}

	// the allowed range runs from the bound to wherever the image is now, whichever side that is
	function clampTowardBound(next: number, current: number, bound: number) {
		const min = Math.min(current, bound);
		const max = Math.max(current, bound);
		const isWithinBound = next === bound;
		return isWithinBound ? next : Math.min(Math.max(next, min), max);
	}
	const handlePointerDown = (e: any) => {
		const stage = e.target.getStage();
		const pointerPos = stage?.getPointerPosition();
		if (!pointerPos || !stage) return;

		const intersections = stage.getAllIntersections(pointerPos);

		if(activePanelMode) {
			if(deleteIndividualPanelMode) {
				setEnableSingleFinger(false);
				return;
			}
			if (enablePanelMove) {
				const isPanelClicked = intersections.some((node: any) => {
					const name = node.name() || node.getAttr('name');
					return typeof name === 'string' && name.includes('panel');
				});
				setClickedOnPanel(isPanelClicked);
				setEnableSingleFinger(false);
				return;
			}
			const touchedPanel = intersections.some((node:any) => {
				const name = node.name() || node.getAttr('name');
				return typeof name === 'string' && (name.includes('panel') || (enableMultipleAddPanel && name.includes('facet')));
			});
			const touchedDeleteRect = stage.find('.delete-rect').length > 0;
			setEnableSingleFinger(!(touchedPanel || touchedDeleteRect));
		} else if(enableEditFacet) {
			setEnableSingleFinger(!pointSelected);
		} else if(addObstructionsMode){
			setEnableSingleFinger(false);
		} else if(treeEditingMode){
			setEnableSingleFinger(deleteMode && !isFlattenAreaMode);
		} else {
			setEnableSingleFinger(true);
		}
	};

	// each tool-slice action re-renders every subscribed component, so only dispatch real changes
	const endMultiFingerPress = () => {
		if (!multiFingersActiveRef.current) return;
		multiFingersActiveRef.current = false;
		dispatch(setMultiFingersPress({enabled: false}));
	};

	const handlePointerUp = () => {
		setEnableSingleFinger(false);
		endMultiFingerPress();
	};

	const handleTouchStart = (e: Konva.KonvaEventObject<TouchEvent>) => {
		if (e.evt.touches.length === 1) {
			e.evt.preventDefault(); // prevents long-press save menu
			return;
		}
		if(e.evt.touches.length > 1) {
			// disable drag synchronously (prop update waits for a re-render); this also cancels
			// any pending/active drag started by the first finger so it doesn't fight the pinch
			stageRef.current?.draggable(false);
			lastDistRef.current = null;
			lastCenterRef.current = null;
			lastAngleRef.current = null;
			if (!multiFingersActiveRef.current) {
				multiFingersActiveRef.current = true;
				dispatch(setMultiFingersPress({enabled: true}));
			}
			setEnableSingleFinger(false);
			return;
		}
	};

	const handleTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
		e.evt.preventDefault();
		const stage = stageRef.current;
		const lastDist = lastDistRef.current;
		const lastCenter = lastCenterRef.current;
		const lastAngle = lastAngleRef.current;
		if (!stage) return;
		if (e.evt.touches.length !== 2) return;
		if (stage.draggable()) stage.draggable(false);

		const touch1 = e.evt.touches[0];
		const touch2 = e.evt.touches[1];

		const newDist = Math.hypot(
			touch1.clientX - touch2.clientX,
			touch1.clientY - touch2.clientY
		);

		const center = {
			x: (touch1.clientX + touch2.clientX) / 2,
			y: (touch1.clientY + touch2.clientY) / 2,
		};

		// Angle of the line between the two touch points — drives 2-finger rotate when rotateStage is on.
		const angle = Math.atan2(
			touch2.clientY - touch1.clientY,
			touch2.clientX - touch1.clientX
		) * (180 / Math.PI);

		if (lastDist !== null && lastCenter !== null) {
			const stageBox = stage.container().getBoundingClientRect();
			const localCenter = { x: center.x - stageBox.left, y: center.y - stageBox.top };
			const lastLocalCenter = { x: lastCenter.x - stageBox.left, y: lastCenter.y - stageBox.top };

			// --- Zoom ---
			const oldScale = stage.scaleX();
			const newScale = oldScale * (newDist / lastDist);

			// --- Rotate (only while rotate-mode is enabled) ---
			let newRotation = stage.rotation();
			if (rotateStage && lastAngle !== null) {
				let rotationDelta = angle - lastAngle;
				rotationDelta = ((rotationDelta + 180) % 360 + 360) % 360 - 180; // normalize to [-180, 180]
				newRotation += rotationDelta;
			}

			// Keep the map point that was under the previous midpoint under the current midpoint at the
			// new scale/rotation — this covers zoom, rotate and pan in one step. Anchoring at the current
			// midpoint and adding the finger delta separately drifts by delta * (zoomFactor - 1) per frame.
			const localPoint = getLocalPoint(stage, lastLocalCenter);
			const offset = rotateVectorDeg({ x: localPoint.x * newScale, y: localPoint.y * newScale }, newRotation);

			stage.rotation(newRotation);
			stage.scale({ x: newScale, y: newScale });
			stage.position({
				x: localCenter.x - offset.x,
				y: localCenter.y - offset.y,
			});

			stage.batchDraw();
		}

		lastDistRef.current = newDist;
		lastCenterRef.current = center;
		lastAngleRef.current = angle;
	};

	const handleTouchEnd = () => {
		const stage = stageRef.current;
		// lastDistRef is set by two-finger moves only; a tap or one-finger pan changes no zoom/rotation
		// (one-finger pans store their position in onDragEnd)
		const pinchMovedStage = lastDistRef.current !== null;
		if (stage && pinchMovedStage) {
			// setZoomLevel stores the position too
			dispatch(
				setZoomLevel({
					scaleBy: stage.scaleX(),
					position: stage.position(),
				})
			);
			dispatch(setStageRotation({ rotation: stage.rotation() }));
		}
		lastDistRef.current = null;
		lastCenterRef.current = null;
		lastAngleRef.current = null;
		setEnableSingleFinger(false);
		endMultiFingerPress();
	};

	useEffect(() => {
		const stage = stageRef.current;
		if (!stage) return;
		const container = stage.container();

		let dragging = false;
		let lastAngle = 0;
		let pivotLocal = { x: 0, y: 0 };

		const getScreenCenter = () => ({ x: stage.width() / 2, y: stage.height() / 2 });

		const angleFromCenter = (clientX: number, clientY: number) => {
			const rect = container.getBoundingClientRect();
			const center = getScreenCenter();
			const localX = clientX - rect.left;
			const localY = clientY - rect.top;
			return Math.atan2(localY - center.y, localX - center.x) * (180 / Math.PI);
		};

		const onPointerMove = (e: PointerEvent) => {
			if (!dragging) return;
			let delta = angleFromCenter(e.clientX, e.clientY) - lastAngle;
			delta = ((delta + 180) % 360 + 360) % 360 - 180;
			lastAngle += delta;
			rotateStageAroundPivot(stage, pivotLocal, getScreenCenter(), stage.rotation() + delta);
			stage.batchDraw();
		};

		const onPointerUp = () => {
			if (!dragging) return;
			dragging = false;
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('pointerup', onPointerUp);
			// rotateStageAroundPivot moves position on every frame of the drag — persist the
			// final position too, or Redux's konvaStagePosition goes stale the moment this ends.
			dispatch(setKonvaStagePosition({ position: stage.position() }));
			dispatch(setStageRotation({ rotation: stage.rotation() }));
		};

		const onPointerDown = (e: PointerEvent) => {
			if (!rotateStage || e.button !== 0) return;
			if (!e.isPrimary) { onPointerUp(); return; } // second finger: the pinch handler takes over
			dragging = true;
			pivotLocal = getLocalPoint(stage, getScreenCenter());
			lastAngle = angleFromCenter(e.clientX, e.clientY);
			window.addEventListener('pointermove', onPointerMove);
			window.addEventListener('pointerup', onPointerUp);
			e.preventDefault();
		};

		container.addEventListener('pointerdown', onPointerDown);
		return () => {
			// Letting go of Alt before the mouse ends rotate mode mid-drag: save the gesture first
			// (onPointerUp also removes the window listeners, and does nothing when not dragging).
			onPointerUp();
			container.removeEventListener('pointerdown', onPointerDown);
		};
	}, [rotateStage, dispatch, stageRef]);

	function handleStageClick(e: Konva.KonvaEventObject<PointerEvent>){
		if (rotateStage) return;
		const stage = e.target.getStage();
		const pointerPos = stage?.getPointerPosition();
		if (!pointerPos || !stage) return;

		const intersections = stage.getAllIntersections(pointerPos);

		const clickedOnTarget = intersections.some((node) =>(
			node.findAncestor(`.${KonvaGroupNames.existingFacetsGroup}`) ||
			node.findAncestor('.azimuth_rotator')) // transformer handle)
		);

		// const clickedInsideFacet =
		// 	e.target.findAncestor(`.${KonvaGroupNames.existingFacetsGroup}`);
		// const clikedOnTransformer =
		// 	e.target.findAncestor('.azimuth_rotator');

		if ((!clickedOnTarget) && selectedRoofIndex) {
			console.log('call reset');
			dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
		}
	}
	return (
		<FlexDiv ref={canvasWrapRef}>
			{!hideSatelliteOverlay && <ModeStripContainer/>}

			{showIrradiance && <MonthButtons />}
			{!org.setting.isB2C ? (!fullScreenEnabled && <FullViewBtn/>) : null}
			<Stage
				scaleX={zoomLevel}
				scaleY={zoomLevel}
				rotation={stageRotation}
				ref={stageRef}
				// pixelRatio={1}
				visible={!threeDViewLoaded}
				draggable={!rotateStage && ((moveStage && !multiFingersPress) || enableSingleFinger)}
				onWheel={handleMouseScroll}
				onContextMenu={(e) => {
					e.evt.preventDefault();
				}}
				onPointerClick={handleStageClick}
				onPointerDown={handlePointerDown}
				onPointerUp={handlePointerUp}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onDragStart={() => {
					if (!stageRef.current || !moveStage) return;
					stageRef.current.container().style.cursor = 'grabbing';
				}}
				onDragEnd={(e) => {
					const stage = stageRef.current;
					if (!stage) return;
					// dragend bubbles up from child nodes (panels, points); only the stage's own drag moves the map
					if (e.target === stage && !fullScreenEnabled) {
						dispatch(setKonvaStagePosition({ position: stage.position() }));
					}
					if (!moveStage) return;
					stage.container().style.cursor = 'grab';
				}}
				dragBoundFunc={setDragBoundToStage}
				style={{
					cursor: cursorStyle,
					userSelect: 'none',
					WebkitUserSelect: 'none',
					msUserSelect: 'none',
					WebkitTouchCallout: 'none',
					WebkitTapHighlightColor: 'transparent',
					touchAction: 'none'
				}}
			>
				{/* Map Layer */}
				<Layer 
					name="map-layer" 
					scale={baseImageScale}
					listening={customImageEditingEnabled && !multiFingersPress}>
					<RasterImage /> 
					{mode === DESIGN_MODES.NEARMAP_NREL_MANUAL && <CustomMapGroup/> }
				</Layer>

				<Layer
					ref={layerRef}
					name='main-layer'
					visible={!hideSatelliteOverlay}
					listening={!customImageEditingEnabled && !hideSatelliteOverlay && !isCanvasLocked}>
					{/* Flatten area fill must stay the base (first) child — everything else renders above it */}
					<FlattenAreaFillLayer />
					<PanelDrawer showAzimuthArrows={mode === DESIGN_MODES.NEARMAP_NREL_MANUAL || groundMountEnabled} />
					{showCustomVectorPanel && <CustomVectorPanel />}
					{showIrradiance && <Irradiance layerRef={layerRef} />}
					<FacetGroup />
					{designType !== 'ROOF' && <Obstructions />}
					{!enableEditFacet && <SetbackArea />}

					{/* Trees */}
					<DsmEditsGroup/>
					{ treeEditingMode && <><TreeDrawer /><FlattenAreaDrawer /></>}

					{(enableAddPanel && !enableMultipleAddPanel) && <CustomPanel />}
					{addObstructionsMode && <ObstructionDrawer />}

					{!enableEditFacet && <SimplifiedConcaveHullLines />}
					<RawConcaveHullLines />
					{/* HIde auto detected special edges & gutters */}
					{designType === 'ROOF' && <GutterLines />}
					{designType === 'ROOF' && <SpecialEdges />} 
					{/* should always be  last node of stage for visibility */}
					{selectedMovablePanels.length > 0 && <SelectedMovablePanelsGroup />}
					<EdgeLength/>
					<FacetLabels />
				</Layer>
			</Stage>
			{/* 
				TODO:
				mve non-canvas elemnts (production strip, mode strip, loader,
				irradiance month btns, move/ zoom btns etc...) into thw ToolContainer wrapper
				(src/components/ToolContainer/ToolContainer.tsx).
				in order to keep the componet specifically for canvas related rendering
				*/}

			{threeDModelEnabled && <div
				style={{
					width: konvaStageDims.width,
					height: konvaStageDims.height,
					position: (showLoader && threeDModelEnabled) ? 'absolute' : 'relative'
				}}>
				{/* fallback is null: the existing showLoader/isThreeDLoader overlay
				    below already covers the 3D load, so a second spinner would stack. */}
				<Suspense fallback={null}>
					<ThreeDView/>
				</Suspense>
			</div>}

			{showLoader && <Loader
				type={isThreeDLoader ? Loader_Type.BAR : Loader_Type.DEFAULT}
				styles={{
					position: 'absolute',
					background: 'var(--overlayBgColor)', top: '0px'
				}}
			/>}

			{designType === 'SOLAR'&& <StripProductionWrap fullScreenEnabled={fullScreenEnabled}>
				<ProductionLegend />
			</StripProductionWrap>}
			{!threeDModelEnabled &&
					<MoveZoomWrapper
						className={((fullScreenEnabled && showIrradiance) || activeRoofTool) ? 'show-irradiance zoomWrapper' : 'zoomWrapper'}
					>
						<CompassButton />
						{!org.setting.isB2C && <RotateButton />}
						<MoveButton />
						<ZoomButtons />
					</MoveZoomWrapper>
			}
		</FlexDiv>
	);
}

export default Tool;
